import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import type { PasswordValidationStatus } from 'firebase/auth'
import { Button } from '@/components/button/Button'
import { CardPageAgreementNotice, CardPageLayout } from '@/components/card-page-layout/CardPageLayout'
import { WorkEmailInput } from '@/components/work-email-input/WorkEmailInput'
import { getWorkEmailValidationMessage, kWorkEmailInvalidAddressMessage } from '@/components/work-email-input/work-email-policy'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/popover/Popover'
import InputFieldFloatingLabel from '@/components/input-field-floating-label/InputFieldFloatingLabel'
import { PasswordRequirements } from './PasswordRequirements'
import { EnvelopeOpenIcon } from '@heroicons/react/24/outline'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import type { AuthCredentials } from './auth-credentials-context'

type CreateAccountPageContentProps = {
  errorMessage: string | null
  submitting: boolean
  passwordStatus: PasswordValidationStatus | null
  recaptcha: ReactNode
  referralCodeActive?: boolean
  referralError?: string | null
  onPasswordChange: (password: string) => void
  onCreateAccount: (email: string, password: string) => void
  onSignIn: (credentials: AuthCredentials) => void
  initialCredentials?: AuthCredentials
}

export function CreateAccountPageContent({
  errorMessage,
  submitting,
  passwordStatus,
  recaptcha,
  referralCodeActive = false,
  referralError = null,
  onPasswordChange,
  onCreateAccount,
  onSignIn,
  initialCredentials,
}: CreateAccountPageContentProps) {
  const [passwordFocused, setPasswordFocused] = useState(false)
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<AuthCredentials>({ defaultValues: initialCredentials ?? { email: '', password: '' } })

  return (
    <CardPageLayout footer={<CardPageAgreementNotice />}>
      <div className="flex flex-col gap-6">
        <h1 className="text-center font-rethink-sans text-xl font-semibold leading-7 text-brand-neutral-950">Create your account</h1>
        {referralCodeActive && (
          <InlineMessage variant="info" icon={<EnvelopeOpenIcon />}>
            Referral code active!
          </InlineMessage>
        )}
        {referralError && <InlineMessage variant="error">{referralError}</InlineMessage>}
        <form className="flex flex-col gap-4" onSubmit={handleSubmit(({ email, password }) => onCreateAccount(email, password))}>
          <WorkEmailInput
            id="email-address"
            required
            disabled={submitting}
            error={errors.email?.message}
            {...register('email', { required: kWorkEmailInvalidAddressMessage, validate: (email) => getWorkEmailValidationMessage(email) ?? true })}
          />
          <Popover open={passwordFocused} onOpenChange={setPasswordFocused}>
            <PopoverAnchor asChild>
              <div>
                <InputFieldFloatingLabel
                  label="Create a password"
                  id="password"
                  required
                  disabled={submitting}
                  registration={register('password', { required: true, onChange: (event) => onPasswordChange(event.target.value) })}
                  inputProps={{
                    type: 'password',
                    autoComplete: 'new-password',
                    required: true,
                    onFocus: () => setPasswordFocused(true),
                    onBlur: () => setPasswordFocused(false),
                    'aria-describedby': passwordFocused ? 'password-requirements' : undefined,
                  }}
                />
              </div>
            </PopoverAnchor>
            <PopoverContent
              width="auto"
              align="start"
              showArrow={false}
              sideOffset={4}
              className="w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] rounded-md border border-brand-neutral-200 p-4 shadow-md"
              onOpenAutoFocus={(event) => event.preventDefault()}
              onCloseAutoFocus={(event) => event.preventDefault()}
              onInteractOutside={(event) => {
                if (event.target instanceof HTMLElement && event.target.id === 'password') event.preventDefault()
              }}
            >
              <PasswordRequirements status={passwordStatus} />
            </PopoverContent>
          </Popover>
          {recaptcha}
          {errorMessage && <InlineMessage variant="error">{errorMessage}</InlineMessage>}
          <Button type="submit" fullWidth loading={submitting}>
            Continue
          </Button>
        </form>
        <p className="flex flex-wrap items-center justify-center gap-1 font-rethink-sans text-sm font-semibold text-brand-neutral-700">
          Already have an account?
          <Button variant="plain" size="sm" className="font-sans" disabled={submitting} onClick={() => onSignIn(getValues())}>
            Sign in
          </Button>
        </p>
      </div>
    </CardPageLayout>
  )
}
